import { ChakraV3 } from '@piccolohealth/ui';
import { DateTime, P } from '@piccolohealth/util';
import React from 'react';
import { useAppContext } from '../../../../hooks/useAppContext';
import { usePermission } from '../../../../hooks/usePermission';
import { EmojiPickerButton } from '../../../generic/EmojiPickerButton';
import { UserAvatar } from '../../../user/UserAvatar';
import type { CommentData } from '../../extensions/comment/Comment';
import { CommentReactionsList } from './CommentReactionsList';

export interface CommentDataView {
  comment: CommentData;
  isReply?: boolean;
  onEditComment: (comment: CommentData) => void;
  onRemoveComment: (comment: CommentData) => void;
  isDisabled?: boolean;
}

export const CommentDataView = (props: CommentDataView) => {
  const { comment, isReply, onRemoveComment, onEditComment } = props;

  const { user } = useAppContext();
  const ref = React.useRef(null);
  const managePermission = usePermission('manage', 'participantDocumentComment');
  // If a user has manage permission (e.g. they're Admin/Support/TeamLeader), they can edit/remove
  // all comments, not just those they are the author of.
  const canEditComment = comment.userId === user.id || managePermission;
  const isDisabled = props.isDisabled || !canEditComment;
  const [content, setContent] = React.useState(comment.content);

  const onAddReaction = React.useCallback(
    (id: string) => {
      const reaction = comment.reactions?.find((reaction) => reaction.id === id);

      if (reaction && reaction.userId === user.id) {
        return;
      }

      onEditComment({
        ...comment,
        reactions: [...(comment.reactions ?? []), { id, username: user.name, userId: user.id }],
      });
    },
    [comment, onEditComment, user.id, user.name],
  );

  const onRemoveReaction = React.useCallback(
    (id: string) => {
      onEditComment({
        ...comment,
        reactions: P.compact(
          (comment.reactions ?? []).map((reaction) => {
            if (reaction.id === id && reaction.userId === user.id) {
              return null;
            }
            return reaction;
          }),
        ),
      });
    },
    [comment, onEditComment, user.id],
  );

  return (
    <ChakraV3.Editable.Root
      key={comment.time}
      ref={ref}
      disabled={isDisabled}
      selectOnFocus={false}
      value={content}
      onValueChange={({ value }) => setContent(value)}
      onValueCommit={() => onEditComment({ ...comment, content })}
      activationMode='none'
      className='group'
      pl={isReply ? 2 : 0}
      ml={isReply ? 2 : 0}
      borderLeftWidth={isReply ? '1px' : 0}
      borderColor='gray.200'
    >
      <ChakraV3.Stack w='full'>
        <ChakraV3.HStack align='center' gap={2}>
          <UserAvatar
            name={comment.username}
            picture={comment.picture}
            size='xs'
            showTooltip={false}
          />
          <ChakraV3.Stack gap={0} overflow='hidden' flexShrink={1}>
            <ChakraV3.Text
              fontSize='sm'
              fontWeight='bold'
              textOverflow='ellipsis'
              whiteSpace='nowrap'
            >
              {comment.username}
            </ChakraV3.Text>
            <ChakraV3.Text fontSize='xs' color='secondary'>
              {DateTime.fromMillis(comment.time).toRelative({
                style: 'narrow',
              })}
            </ChakraV3.Text>
          </ChakraV3.Stack>
          <ChakraV3.Spacer />
          {!isDisabled && <ChakraV3.EditableControls onRemove={() => onRemoveComment(comment)} />}
        </ChakraV3.HStack>
        <ChakraV3.Flex minH='8' align='center' mt={1}>
          <ChakraV3.Editable.Preview
            px={3}
            py={1}
            borderColor='transparent'
            borderWidth='1px'
            wordBreak='break-word'
            lineHeight='20px'
            fontSize='sm'
          />
          <ChakraV3.Editable.Textarea
            as={ChakraV3.Textarea}
            {...{ autoresize: true }}
            rows={1}
            px={3}
            py={1}
            bg='white'
            fontSize='sm'
            lineHeight='20px'
            h='auto'
            _focusVisible={{
              boxShadow: 'none',
            }}
          />
        </ChakraV3.Flex>
        <ChakraV3.HStack gap={0}>
          <CommentReactionsList
            reactions={comment.reactions ?? []}
            onAddReaction={onAddReaction}
            onRemoveReaction={onRemoveReaction}
            isDisabled={props.isDisabled}
          />
          <EmojiPickerButton onSelect={({ id }) => onAddReaction(id)} disabled={props.isDisabled} />
        </ChakraV3.HStack>
      </ChakraV3.Stack>
    </ChakraV3.Editable.Root>
  );
};
